import React, { useEffect, useRef, useState, useContext } from 'react';
import * as d3 from 'd3';
import { useStore } from '../../../store/useStore';
import { ThemeContext } from 'styled-components';
import * as S from '../../Charts/BarChart/BarChart.style';

type Props = {
  size: { width: number; height: number };
};

interface MarketNode {
  name: string;
  parent: string;
  value: number;
  present: boolean;
}

const ABSENT_EPSILON = 0.01;

export const MarketActivity = ({ size }: Props) => {
  const chartRef = useRef(null);
  const [market, setMarket] = useState<MarketNode[]>([]);
  const theme = useContext(ThemeContext);
  const cellularState = useStore((state) => state.cellularState);

  useEffect(() => {
    const seed: MarketNode[] = Array.from(Array(256)).map((c, i) => ({
      name: i.toString(),
      parent: 'Origin',
      value: cellularState[i] ? cellularState[i] : ABSENT_EPSILON,
      present: Boolean(cellularState[i]),
    }));
    seed.unshift({ name: 'Origin', parent: '', value: 0, present: true });
    setMarket(seed);
  }, [cellularState]);

  useEffect(() => {
    drawChart();
  }, [theme, market, size]);

  const drawChart = () => {
    if (!theme) return;

    // Guard: Don't draw if size is invalid
    if (!size.width || !size.height || size.width <= 0 || size.height <= 0) {
      return;
    }

    // set the dimensions and margins of the graph
    const margin = { top: 10, right: 10, bottom: 10, left: 10 };
    const width = size.width;
    const height = size.height;
    d3.select('#activity').selectAll('svg').remove();
    // append the svg object to the body of the page
    const svg = d3
      .select('#activity')
      .append('svg')
      .attr('width', width)
      .attr('height', height - margin.top - margin.bottom)
      .style('border', `5px solid ${theme.primary}`)
      .append('g')
      .attr('transform', 'translate(-5, -10)');

    // Read data

    if (!market.length) return;
    // stratify the data: reformatting for d3.js
    const stratifyFn = d3
      .stratify<MarketNode>()
      .id(function (d) {
        return d.name;
      })
      .parentId(function (d) {
        return d.parent;
      });
    const root = stratifyFn(market);
    root.sum(function (d) {
      return +d.value;
    });

    // Then d3.treemap computes the position of each element of the hierarchy
    // The coordinates are added to the root object above
    const treemapFn = d3.treemap<MarketNode>().size([width, height]).padding(4);
    treemapFn(root);

    // use this information to add rectangles:
    // After treemap(), nodes have rectangular layout properties
    const leaves = root.leaves() as d3.HierarchyRectangularNode<MarketNode>[];
    svg
      .selectAll('rect')
      .data(leaves)
      .enter()
      .append('rect')
      .attr('x', function (d) {
        return d.x0;
      })
      .attr('y', function (d) {
        return d.y0;
      })
      .attr('width', function (d) {
        return d.x1 - d.x0;
      })
      .attr('height', function (d) {
        return d.y1 - d.y0;
      })
      .style('stroke', theme.text)
      .style('stroke-opacity', (d) => (d.data.present ? 0.9 : 0.26))
      .style('stroke-width', (d) => (d.data.present ? 2.2 : 1))
      .style('stroke-dasharray', (d) => (d.data.present ? 'none' : '2 2'))
      .style('fill', theme.text)
      .style('fill-opacity', (d) => (d.data.present ? 1 : 0));
  };

  return (
    <S.ChartSpace>
      <div ref={chartRef} id="activity"></div>
    </S.ChartSpace>
  );
};
